/* Exercise screens: start card, question stage (with input toggle, tools, hints) and results. */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useGo } from '../../app/navigation';
import { useMicState } from '../../core/mic';
import { levelState } from '../../core/store';
import { fmtTime, scrollTop } from '../../core/util';
import { Btn, IconBtn } from '../../ui/Button';
import { ErrorBoundary } from '../../ui/ErrorBoundary';
import { Icon } from '../../ui/icons';
import { Rich } from '../../ui/Rich';
import { modStyle } from '../../ui/style';
import { ProgressBar, Stars } from '../../ui/widgets';
import { MODNAME } from '../module/info';
import { MicBlock } from './MicBlock';
import { ChoiceArea } from './questions/ChoiceArea';
import { ChordArea } from './questions/ChordArea';
import { HoldArea } from './questions/HoldArea';
import { NotesArea } from './questions/NotesArea';
import { type ActiveQuestion, ExerciseSession } from './session';
import type { ExerciseConfig } from './types';

function useSession(session: ExerciseSession): void {
  useSyncExternalStore(session.subscribe, session.getVersion);
}

/** Runs an exercise. `config` is called again for "Tekrar" so seeded generators restart too. */
export function ExerciseRunner({ config }: { config: () => ExerciseConfig }) {
  const go = useGo();
  const make = () => {
    const cfg = config();
    return new ExerciseSession(cfg, () => go(cfg.back || cfg.module));
  };
  const [session, setSession] = useState(make);
  useSession(session);
  useEffect(() => {
    session.attach();
    if (session.cfg.skipStart) session.begin();
    return () => session.detach();
  }, [session]);
  const retry = () => {
    scrollTop();
    setSession(make());
  };
  const cfg = session.cfg;
  return (
    <div className="ex-page" style={modStyle(cfg.module)}>
      {session.phase === 'start' ? <StartScreen session={session} /> : null}
      {session.phase === 'run' ? <RunView session={session} /> : null}
      {session.phase === 'results' ? <Results session={session} onRetry={retry} /> : null}
    </div>
  );
}

function StartScreen({ session }: { session: ExerciseSession }) {
  const { cfg } = session;
  const go = useGo();
  const mic = useMicState();
  const best = levelState(cfg.key);
  const challenge = cfg.mode === 'challenge';
  return (
    <div className="ex-start">
      <button type="button" className="link-back" onClick={() => go(cfg.back || cfg.module)}>
        <Icon name="left" />
        <span>{MODNAME[cfg.module] || 'Geri'}</span>
      </button>
      <div className="ex-start-card">
        <div className="eyebrow">
          {challenge ? 'Meydan okuma' : `Antrenman${cfg.levelNo ? ` · Seviye ${cfg.levelNo}` : ''}`}
        </div>
        <h1 className="ex-start-title">{cfg.title}</h1>
        {cfg.desc ? (
          <p className="ex-start-desc">
            <Rich text={cfg.desc} />
          </p>
        ) : null}
        {cfg.rules?.length ? (
          <ul className="ex-rules">
            {cfg.rules.map((r) => (
              <li key={r}>
                <Rich text={r} />
              </li>
            ))}
          </ul>
        ) : null}
        {best ? (
          <div className="ex-best">
            <Stars n={best.stars || 0} />
            {challenge ? (
              <span>En iyi skor: {best.best || 0}</span>
            ) : (
              <span>En iyi doğruluk: %{Math.round((best.acc || 0) * 100)}</span>
            )}
          </div>
        ) : null}
        {session.needsMic ? <MicBlock screenNote={cfg.input === 'notes' ? 'ya da ekrandaki sapı kullan' : ''} /> : null}
        {session.needsMic && cfg.input === 'voice' && mic !== 'on' ? (
          <p className="mb-note">Mikrofon olmadan bu egzersizi kendin değerlendirerek yapabilirsin.</p>
        ) : null}
        <div className="ex-start-actions">
          <Btn kind="primary" size="lg" icon="play" onClick={session.begin}>
            Başla
          </Btn>
        </div>
      </div>
    </div>
  );
}

function Head({ session }: { session: ExerciseSession }) {
  const { cfg, st } = session;
  let progText: string;
  let frac: number;
  if (cfg.timeLimit) {
    const left = session.timeLeft();
    progText = fmtTime(left);
    frac = left / cfg.timeLimit;
  } else if (cfg.total) {
    // the question on screen, also while its result is shown (answered + 1 ran one ahead)
    progText = `${Math.min(Math.max(st.i, 1), cfg.total)} / ${cfg.total}`;
    frac = st.answered / cfg.total;
  } else {
    progText = `${Math.max(st.i, 1)}. tur`;
    frac = 0;
  }
  return (
    <>
      <div className="ex-head">
        <IconBtn icon="x" label="Çık" onClick={() => session.finish(true)} />
        <div className="ex-titles">
          <div className="ex-title">{cfg.title}</div>
          <div className="ex-sub">
            {(MODNAME[cfg.module] || '') +
              (cfg.levelNo ? ` · Seviye ${cfg.levelNo}` : cfg.mode === 'challenge' ? ' · Meydan okuma' : '')}
          </div>
        </div>
        <div className="ex-score">
          {st.lives != null ? (
            <span className="ex-lives" role="img" aria-label={`${st.lives} can`}>
              {Array.from({ length: cfg.lives || 3 }, (_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length list
                <span key={i} className={`life${i < (st.lives ?? 0) ? ' on' : ''}`} />
              ))}
            </span>
          ) : null}
          <span className="ex-pts" title="Puan">
            <Icon name="star" />
            {String(st.mode === 'challenge' ? st.score : st.xp)}
          </span>
          {st.streak >= 2 ? (
            <span className="ex-streak" title="Seri">
              <Icon name="flame" />
              {String(st.streak)}
            </span>
          ) : null}
        </div>
      </div>
      <div className="ex-bar">
        <ProgressBar frac={frac} />
        <span className="ex-prog">{progText}</span>
      </div>
    </>
  );
}

function InputToggle({ session }: { session: ExerciseSession }) {
  // sung answers without a microphone are judged by the learner, except a melody tapped on the keyboard
  const voice = session.cfg.input === 'voice' && session.current?.q.type !== 'notes';
  const mode = session.st.inputMode;
  return (
    <div className="input-toggle">
      <span className="it-label">Giriş:</span>
      <button
        type="button"
        className={`it-opt${mode === 'mic' ? ' on' : ''}`}
        onClick={() => session.setInputMode('mic')}
      >
        <Icon name="mic" />
        <span>Mikrofon</span>
      </button>
      <button
        type="button"
        className={`it-opt${mode === 'screen' ? ' on' : ''}`}
        onClick={() => session.setInputMode('screen')}
      >
        <Icon name={voice ? 'check' : 'grid'} />
        <span>{voice ? 'Kendim değerlendiririm' : 'Ekran'}</span>
      </button>
    </div>
  );
}

function QuestionArea({ cq }: { cq: ActiveQuestion }) {
  const { q, api } = cq;
  switch (q.type) {
    case 'choice':
      return <ChoiceArea q={q} api={api} />;
    case 'notes':
      return <NotesArea q={q} api={api} />;
    case 'hold':
      return <HoldArea q={q} api={api} />;
    case 'chord':
      return <ChordArea q={q} api={api} />;
    case 'custom':
      return <>{q.render(api)}</>;
  }
}

function QuestionCard({ session, cq }: { session: ExerciseSession; cq: ActiveQuestion }) {
  const { q } = cq;
  const nextBtn = useRef<HTMLDivElement>(null);
  const foot = useRef<HTMLDivElement>(null);
  const hints = q.hints || [];
  const challenge = session.st.mode === 'challenge';
  useEffect(() => {
    if (!cq.done) return;
    nextBtn.current?.querySelector('button')?.focus({ preventScroll: true });
    // after a tall question on a phone the result and "Sonraki" sit below the fold: bring them up
    foot.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [cq.done]);
  return (
    <div className="ex-card" data-question={cq.id}>
      <div className="ex-top">
        {q.play ? (
          <button
            type="button"
            className={`listen${cq.playing ? ' on' : ''}`}
            aria-label="Dinle"
            onClick={() => session.play()}
          >
            <span className="listen-rings" />
            <Icon name="play" />
          </button>
        ) : null}
        <div className="ex-prompt">
          <div className="ex-q">
            <Rich text={cq.prompt || 'Dinle'} />
          </div>
          {q.info ? (
            <div className="ex-info">
              <Rich text={q.info} />
            </div>
          ) : null}
        </div>
      </div>
      <div className="ex-area">
        <ErrorBoundary
          key={`${cq.id}.${cq.mount}`}
          onError={(e) => cq.api.feedback(`Bu soru yüklenemedi: ${e.message}`, 'bad')}
        >
          <QuestionArea cq={cq} />
        </ErrorBoundary>
      </div>
      <div className={`ex-feedback${session.feedback?.tone ? ` t-${session.feedback.tone}` : ''}`} aria-live="polite">
        {session.feedback?.content ?? null}
      </div>
      <div className="ex-foot" ref={foot}>
        <div className="ex-tools">
          {q.play ? (
            <Btn kind="subtle" icon="replay" onClick={() => session.play()}>
              Tekrar dinle
            </Btn>
          ) : null}
          {q.playSlow ? (
            <Btn kind="subtle" onClick={() => session.play(true)}>
              Yavaş
            </Btn>
          ) : null}
          {q.extraTools?.map((t) => (
            <Btn key={t.label} kind="subtle" icon={t.icon} onClick={() => t.run(cq.api)}>
              {t.label}
            </Btn>
          ))}
          {hints.length ? (
            <Btn
              kind="subtle"
              icon="bulb"
              hidden={cq.done}
              disabled={cq.hintIdx >= hints.length}
              onClick={session.hint}
            >
              İpucu
            </Btn>
          ) : null}
          <Btn kind="subtle" icon="skip" hidden={cq.done} onClick={session.skip}>
            {challenge ? 'Pas' : 'Geç'}
          </Btn>
        </div>
        <div ref={nextBtn} style={{ display: 'contents' }}>
          {cq.ended ? (
            <Btn
              kind="primary"
              icon="chart"
              className="ex-next"
              hidden={!cq.done}
              onClick={() => session.finish(false)}
            >
              Sonuçlar
            </Btn>
          ) : (
            <Btn kind="primary" icon="right" className="ex-next" hidden={!cq.done} onClick={session.nextQuestion}>
              {challenge && session.cfg.total == null ? 'Devam' : 'Sonraki'}
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

function RunView({ session }: { session: ExerciseSession }) {
  const cq = session.current;
  return (
    <>
      <Head session={session} />
      {session.needsMic ? <InputToggle session={session} /> : null}
      <div className="ex-stage">{cq ? <QuestionCard session={session} cq={cq} /> : null}</div>
    </>
  );
}

function Results({ session, onRetry }: { session: ExerciseSession; onRetry: () => void }) {
  const go = useGo();
  const { cfg, st } = session;
  const r = session.result;
  if (!r) return null;
  const isCh = cfg.mode === 'challenge';
  const big = isCh ? String(st.score) : `%${Math.round(r.acc * 100)}`;
  const title = r.aborted
    ? 'Yarıda bıraktın'
    : r.stars === 3
      ? 'Mükemmel iş!'
      : r.stars === 2
        ? 'Çok iyi gidiyorsun'
        : r.stars === 1
          ? 'Güzel başlangıç'
          : 'Biraz daha pratik';
  return (
    <div className="results">
      <div className="rs-top">
        <Stars n={r.stars} />
        <h1>{title}</h1>
        {r.newBest && isCh && !r.aborted ? <div className="rs-badge">Yeni rekor!</div> : null}
        {!isCh && !r.aborted ? (
          <p className="rs-note">
            {r.stars >= 2
              ? 'Bir sonraki seviyeye geçmeye hazırsın.'
              : 'Aynı seviyeyi bir kez daha denemek kulağını hızla oturtur.'}
          </p>
        ) : null}
      </div>
      <div className="rs-stats">
        <div className="rs-stat">
          <span className="rs-k">{isCh ? cfg.scoreLabel || 'Skor' : 'Doğruluk'}</span>
          <span className="rs-v">{big}</span>
        </div>
        <div className="rs-stat">
          <span className="rs-k">Kazanılan XP</span>
          <span className="rs-v">+{st.xp}</span>
        </div>
        <div className="rs-stat">
          <span className="rs-k">En uzun seri</span>
          <span className="rs-v">{st.bestStreak}</span>
        </div>
        {isCh && r.prev.best != null ? (
          <div className="rs-stat">
            <span className="rs-k">Önceki en iyi</span>
            <span className="rs-v">{r.prev.best || 0}</span>
          </div>
        ) : null}
      </div>
      {st.review.length ? (
        <div className="rs-review">
          <h3>Tekrar bakmaya değer</h3>
          <ul>
            {st.review.slice(0, 8).map((it, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: review entries may repeat
              <li key={i}>
                <span>{it.label}</span>
                {it.answer && it.answer !== it.label ? (
                  <span className="rs-ans">
                    <Rich text={it.answer} />
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="rs-actions">
        <Btn kind="ghost" icon="replay" onClick={onRetry}>
          Tekrar
        </Btn>
        {cfg.next ? (
          <Btn kind="primary" icon="right" onClick={() => go(cfg.next as string)}>
            Sonraki seviye
          </Btn>
        ) : null}
        <Btn kind="ghost" onClick={() => go(cfg.back || cfg.module)}>
          Bölüme dön
        </Btn>
      </div>
    </div>
  );
}
